import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Tooltip, Progress, Select, Empty, Alert, Grid, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, ArrowLeftOutlined, ReloadOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import webmailService from '../../services/webmailService';
import { userPath } from '../../../shared/config/paths';

const { Text } = Typography;
const GiB = 1024 * 1024 * 1024;
const MiB = 1024 * 1024;
const ok = (res) => res && (res.code === 0 || res.success === true);

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

const Users = () => {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const domainIdFromUrl = searchParams.get('domain_id');

    const [users, setUsers] = useState([]);
    const [domains, setDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(domainIdFromUrl ? Number(domainIdFromUrl) : null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();

    const domain = useMemo(() => domains.find(d => d.local_id === selectedDomain) || null, [domains, selectedDomain]);
    const domainName = domain?.name || searchParams.get('domain_name') || '';
    // Mailboxes can only be created once the domain is live on the mail node.
    const notLive = domain && domain.dns && domain.dns.ownership_verified === false;

    const fetchDomains = useCallback(async () => {
        const res = await webmailService.getDomains();
        if (!ok(res)) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setDomains(list);
        setSelectedDomain(cur => cur || list[0]?.local_id || null);
    }, []);

    const fetchUsers = useCallback(async () => {
        if (!selectedDomain) return;
        setLoading(true);
        const res = await webmailService.getUsers({ domain_id: selectedDomain });
        if (ok(res)) setUsers(Array.isArray(res.data) ? res.data : []);
        else message.error(res?.message || res?.error || t('Failed to load'));
        setLoading(false);
    }, [selectedDomain, t]);

    useEffect(() => { fetchDomains(); }, [fetchDomains]);
    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // Storage the domain still has for mailbox quotas (plan pool minus what is allocated).
    const allocatable = (exceptId) => {
        const pool = Number(domain?.quota_bytes || 0);
        if (!pool) return null;
        const used = users.filter(u => u.id !== exceptId).reduce((s, u) => s + Number(u.quota_bytes || 0), 0);
        return Math.max(0, pool - used);
    };
    const maxGb = (exceptId) => {
        const a = allocatable(exceptId);
        return a === null ? 100 : Math.floor((a / GiB) * 10) / 10;
    };

    const openAdd = () => {
        setEditingUser(null);
        form.resetFields();
        form.setFieldsValue({ active: true, quota_gb: Math.min(1, maxGb(null)) });
        setModalVisible(true);
    };

    const openEdit = (record) => {
        setEditingUser(record);
        form.setFieldsValue({
            display_name: record.display_name,
            active: !!record.active,
            quota_gb: Math.round((Number(record.quota_bytes || 0) / GiB) * 10) / 10,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        const res = await webmailService.deleteUser(id);
        if (ok(res)) { message.success(t('Deleted')); fetchUsers(); }
        else message.error(res?.message || res?.error || t('Deletion failed'));
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        // whole megabytes: the API takes an integer byte count
        const data = {
            domain_id: selectedDomain,
            display_name: values.display_name,
            active: values.active,
            quota_bytes: Math.max(1, Math.round(Number(values.quota_gb || 0) * 1024)) * MiB,
        };
        // The form shows GB with one decimal; when the number was not touched keep the exact quota
        // (100 MB shows as 0.1 GB and would come back as 102 MB).
        if (editingUser && Number(values.quota_gb) === Math.round((Number(editingUser.quota_bytes || 0) / GiB) * 10) / 10) {
            delete data.quota_bytes;
        }
        let res;
        if (editingUser) {
            res = await webmailService.updateUser(editingUser.id, data);
        } else {
            res = await webmailService.createUser({ ...data, username: values.username, password: values.password });
        }
        setSubmitting(false);
        if (ok(res)) {
            message.success(editingUser ? t('Saved') : t('Mailbox {{email}} created', { email: res.data?.email || `${values.username}@${domainName}` }));
            setModalVisible(false);
            fetchUsers();
        } else {
            message.error(res?.message || res?.error || t('Operation failed'));
        }
    };

    const handlePasswordSubmit = async (values) => {
        setSubmitting(true);
        const res = await webmailService.updateUser(editingUser.id, { password: values.password });
        setSubmitting(false);
        if (ok(res)) { message.success(t('Password reset')); setPasswordModalVisible(false); }
        else message.error(res?.message || res?.error || t('Reset failed'));
    };

    const openWebmail = async (user) => {
        message.loading({ content: t('Opening webmail…'), key: 'webmail-login' });
        const res = await webmailService.getLoginToken(user.id);
        if (ok(res)) {
            const { token, webmail_url } = res.data;
            window.open(`${webmail_url}/#/login?token=${encodeURIComponent(token)}`, '_blank', 'noopener');
            message.destroy('webmail-login');
        } else {
            message.error({ content: res?.message || res?.error || t('Could not open'), key: 'webmail-login' });
        }
    };

    // Used / quota in words, bar underneath (the quota is this mailbox's share of the plan).
    const storageBar = (u) => {
        const pct = Math.round(((u.used_bytes || 0) / (u.quota_bytes || 1)) * 100);
        return (
            <div>
                <Text style={{ fontSize: 12 }}>{formatBytes(u.used_bytes || 0)} / {formatBytes(u.quota_bytes || 0)}</Text>
                <Progress percent={pct} size="small" showInfo={false} status={pct > 90 ? 'exception' : 'normal'} style={{ margin: 0 }} />
            </div>
        );
    };

    // Domain summary: quotas handed out vs the plan, and what is actually stored.
    const pool = Number(domain?.quota_bytes || 0);
    const allocated = users.reduce((s, u) => s + Number(u.quota_bytes || 0), 0);
    const usedTotal = users.reduce((s, u) => s + Number(u.used_bytes || 0), 0);
    const overAllocated = pool > 0 && allocated > pool;
    const summary = selectedDomain && users.length > 0 && (
        <Alert type={overAllocated ? 'warning' : 'info'} showIcon style={{ marginBottom: 12 }}
            message={pool > 0
                ? t('Quotas allocated: {{allocated}} of {{plan}} · Stored: {{used}}', { allocated: formatBytes(allocated), plan: formatBytes(pool), used: formatBytes(usedTotal) })
                : t('Quotas allocated: {{allocated}} · Stored: {{used}}', { allocated: formatBytes(allocated), used: formatBytes(usedTotal) })}
            description={overAllocated ? t('Mailbox quotas add up to more than the plan. Lower a quota before adding a mailbox or raising another quota.') : null} />
    );

    const actions = (u, block = false) => (
        <Space size={4} style={block ? { width: '100%' } : undefined} wrap>
            <Tooltip title={t('Reset password')}><Button size={block ? 'middle' : 'small'} icon={<KeyOutlined />} onClick={() => { setEditingUser(u); passwordForm.resetFields(); setPasswordModalVisible(true); }} /></Tooltip>
            <Tooltip title={t('Edit')}><Button size={block ? 'middle' : 'small'} icon={<EditOutlined />} onClick={() => openEdit(u)} /></Tooltip>
            <Tooltip title={t('Open webmail')}><Button size={block ? 'middle' : 'small'} type="primary" icon={<MailOutlined />} onClick={() => openWebmail(u)}>{block ? t('Open webmail') : null}</Button></Tooltip>
            <Popconfirm title={t('Delete mailbox {{email}}?', { email: u.email })} description={t('All mail in this mailbox will be permanently deleted')}
                okText={t('Delete')} okButtonProps={{ danger: true }} cancelText={t('Cancel')} onConfirm={() => handleDelete(u.id)}>
                <Button size={block ? 'middle' : 'small'} danger icon={<DeleteOutlined />} />
            </Popconfirm>
        </Space>
    );

    const statusTag = (u) => u.active ? <Tag color="green">{t('Enable')}</Tag> : <Tag color="red">{t('Disable')}</Tag>;

    const columns = [
        { title: t('Email'), dataIndex: 'email', render: (email, u) => <Space><UserOutlined /><strong>{email}</strong>{statusTag(u)}</Space> },
        { title: t('Display name'), dataIndex: 'display_name' },
        { title: t('Used / quota'), width: 180, render: (_, u) => storageBar(u) },
        { title: t('Messages'), dataIndex: 'used_messages', width: 100, render: (n) => (n || 0).toLocaleString() },
        { title: t('Actions'), width: 170, render: (_, u) => actions(u) },
    ];

    const emptyText = selectedDomain ? t('No mailboxes yet. Click "Add mailbox" to create one.') : t('Please select a domain first');

    return (
        <Card
            styles={isMobile ? { body: { padding: 12 } } : undefined}
            title={
                <Space>
                    {domainIdFromUrl && <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/webmail/domains'))} aria-label={t('Back')} />}
                    <span>{t('Mailboxes')}</span>
                </Space>
            }
            extra={!isMobile && (
                <Space>
                    <Select style={{ width: 220 }} value={selectedDomain} onChange={setSelectedDomain} placeholder={t('Select a domain')}
                        showSearch optionFilterProp="label" options={domains.map(d => ({ value: d.local_id, label: d.name }))} />
                    <Tooltip title={t('Refresh')}><Button icon={<ReloadOutlined />} onClick={fetchUsers} /></Tooltip>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} disabled={!selectedDomain || notLive}>{t('Add mailbox')}</Button>
                </Space>
            )}
        >
            {isMobile && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <Select style={{ flex: 1, minWidth: 0 }} value={selectedDomain} onChange={setSelectedDomain} placeholder={t('Select a domain')}
                        options={domains.map(d => ({ value: d.local_id, label: d.name }))} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} disabled={!selectedDomain || notLive} aria-label={t('Add mailbox')} />
                </div>
            )}

            {notLive && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                    message={t('Mailboxes can be created once domain ownership is verified')}
                    action={<Button size="small" onClick={() => navigate(userPath('/webmail/domains'))}>{t('Verify now')}</Button>} />
            )}

            {summary}

            {isMobile ? (
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    {!loading && users.length === 0 && <Empty description={emptyText} />}
                    {users.map(u => (
                        <Card key={u.id} size="small" styles={{ body: { padding: 12 } }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <Text strong style={{ wordBreak: 'break-all' }}>{u.email}</Text>{statusTag(u)}
                            </div>
                            {u.display_name ? <Text type="secondary" style={{ fontSize: 12 }}>{u.display_name}</Text> : null}
                            <div style={{ marginTop: 6 }}>{storageBar(u)}</div>
                            <div style={{ marginTop: 8 }}>{actions(u, true)}</div>
                        </Card>
                    ))}
                </Space>
            ) : (
                <Table columns={columns} dataSource={users} rowKey="id" loading={loading} scroll={{ x: 760 }}
                    locale={{ emptyText: <Empty description={emptyText} /> }} pagination={{ pageSize: 10, hideOnSinglePage: true }} />
            )}

            <Modal
                title={editingUser ? t('Edit mailbox · {{email}}', { email: editingUser.email }) : t('Add mailbox')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 480}
                style={isMobile ? { top: 12, maxWidth: 'calc(100vw - 24px)' } : undefined}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editingUser && (
                        <>
                            <Form.Item name="username" label={t('Email address')} rules={[
                                { required: true, message: t('Enter the address name') },
                                { pattern: /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/i, message: t('Only letters, numbers, dots, underscores, and hyphens are allowed') },
                            ]} normalize={(v) => (v || '').trim().replace(new RegExp(`@${(domainName || '').replace(/\./g, '\\.')}$`, 'i'), '')}>
                                <Input addonAfter={`@${domainName}`} placeholder="name" autoComplete="off" />
                            </Form.Item>
                            <Form.Item name="password" label={t('Password')} rules={[{ required: true, message: t('Please enter a password') }, { min: 8, message: t('At least 8 characters') }]}>
                                <Input.Password autoComplete="new-password" />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="display_name" label={t('Display name')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="quota_gb" label={t('Storage quota (GB)')}
                        extra={allocatable(editingUser?.id) !== null ? t('{{gb}} GB available in this domain', { gb: maxGb(editingUser?.id) }) : null}
                        rules={[{ required: true, message: t('Enter a quota') }, {
                            // Only a larger quota has to fit in what is left; keeping or lowering is always fine
                            // (a hard max would cut an over-allocated mailbox down, e.g. 50 GB shown as 0.1).
                            validator: (_, v) => {
                                const cur = editingUser ? Number(editingUser.quota_bytes || 0) / GiB : 0;
                                if (v == null || v <= cur + 0.05 || allocatable(editingUser?.id) === null || v <= maxGb(editingUser?.id)) return Promise.resolve();
                                return Promise.reject(new Error(t('Only {{gb}} GB left in this domain', { gb: maxGb(editingUser?.id) })));
                            },
                        }]}>
                        <InputNumber min={0.1} step={0.5} precision={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="active" valuePropName="checked" label={t('Status')}>
                        <Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={submitting}>{t('Save')}</Button>
                        <Button onClick={() => setModalVisible(false)} disabled={submitting}>{t('Cancel')}</Button>
                    </Space>
                </Form>
            </Modal>

            <Modal
                title={t('Reset password · {{email}}', { email: editingUser?.email || '' })}
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 420}
                style={isMobile ? { top: 12, maxWidth: 'calc(100vw - 24px)' } : undefined}
                destroyOnClose
            >
                <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit}>
                    <Form.Item name="password" label={t('New password')} rules={[{ required: true, message: t('Please enter a new password') }, { min: 8, message: t('At least 8 characters') }]}>
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label={t('Confirm password')} dependencies={['password']} rules={[
                        { required: true, message: t('Please enter your password again') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                return !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error(t('Passwords do not match')));
                            },
                        }),
                    ]}>
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={submitting}>{t('Reset password')}</Button>
                        <Button onClick={() => setPasswordModalVisible(false)} disabled={submitting}>{t('Cancel')}</Button>
                    </Space>
                </Form>
            </Modal>
        </Card>
    );
};

export default Users;
